// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BrandedDecorativeDivider from "@synd/XDL/Icons/BrandedDecorativeDivider";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { mergeWithCss } from "@synd/XDL/Utilities";
import React, { FC } from "react";

interface OwnProps {
    showBottomBorder?: boolean;
    inNewTab?: boolean;
    variant?: keyof ThemeTypography;
    title?: string;
    titleAlignment?: "left" | "center" | "right";
    titleStyle?: string;
    titleColor?: string;
    linkColor?: string;
    buttonTitle?: string;
    buttonLink?: string;
    onClick?: () => void;
    titleContentSpace?: number;
    extendedStyles?: StyledTitleStyles;
    iconSrc?: IconPresentationProps["src"];
}

type Props = OwnProps;

export interface StyledTitleStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    container?: InjectableCss;
    link?: LinkPresentationProps;
    smallLink?: LinkPresentationProps;
    divider?: IconPresentationProps;
    linkIcon?: IconPresentationProps;
}

export const styledTitleStyles: StyledTitleStyles = {};

const StyledTitle: FC<Props> = ({
    showBottomBorder,
    inNewTab,
    variant,
    title,
    titleAlignment = "center",
    titleStyle = fontConfig.secondaryFontFamily,
    titleColor,
    linkColor,
    buttonTitle,
    buttonLink,
    onClick,
    titleContentSpace,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(styledTitleStyles, extendedStyles));
    const history = useHistory();

    const navigate = () => {
        onClick?.();

        if (inNewTab) {
            window.open(buttonLink, "_blank", "noreferrer");
        } else if (buttonLink?.startsWith("http")) {
            window.location.href = buttonLink;
        } else if (buttonLink) {
            history.push(buttonLink);
        }
    };

    return (
        <StyledWrapper {...styles.container} {...{ titleContentSpace, showBottomBorder }}>
            <Typography
                variant={variant}
                as="h2"
                {...styles.title}
                color={titleColor}
                fontFamily={titleStyle}
                {...{ titleAlignment }}
            >
                {title}
            </Typography>

            {!!buttonTitle && !!buttonLink && (
                <Link {...styles.link} onClick={navigate} color={linkColor || styles.link?.color}>
                    <Icon {...styles.linkIcon} />
                    {buttonTitle}
                </Link>
            )}
            {showBottomBorder && (
                <Icon
                    src={BrandedDecorativeDivider}
                    size={15}
                    color="primary"
                    {...styles.divider}
                    {...{ titleContentSpace }}
                />
            )}
        </StyledWrapper>
    );
};

export default StyledTitle;
